import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LifeBuoy, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const RestoreRequestPanel = ({ email: initialEmail, onBack }) => {
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/auth/restore-request`, { email, password, message });
      setSent(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send the request');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-300"
      data-testid="restore-request-panel">
      <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-5">
        <LifeBuoy className="w-6 h-6 text-amber-400" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-1.5">Account scheduled for deletion</h2>
      {sent ? (
        <div data-testid="restore-request-sent">
          <p className="text-slate-300 text-sm flex items-start gap-2 mt-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />{sent.message}
          </p>
          <p className="text-slate-500 text-xs mt-3">
            Your data is kept until {new Date(sent.purge_after).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}.
          </p>
        </div>
      ) : (
        <>
          <p className="text-slate-400 text-sm mb-6">
            An admin deactivated this account. Your data is still safe for now — confirm your password and we'll ask an admin to restore it.
          </p>
          <form onSubmit={submit} className="space-y-3">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
              className="bg-slate-800 border-slate-700 text-white" data-testid="restore-request-email" />
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
              className="bg-slate-800 border-slate-700 text-white" data-testid="restore-request-password" />
            <Textarea value={message} maxLength={1000} onChange={(e) => setMessage(e.target.value)} rows={3}
              placeholder="Anything the admin should know? (optional)"
              className="bg-slate-800 border-slate-700 text-white" data-testid="restore-request-message" />
            <Button type="submit" disabled={sending} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              data-testid="restore-request-submit">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Request restore'}
            </Button>
          </form>
          <p className="text-slate-500 text-xs mt-3">Signed up with Google? Email support@solelytax.ai to restore your account.</p>
        </>
      )}
      <button type="button" onClick={onBack} data-testid="restore-request-back"
        className="flex items-center gap-1.5 text-slate-500 hover:text-slate-300 text-sm mt-5 transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
      </button>
    </div>
  );
};

export default RestoreRequestPanel;
